/**
 * FirmLogo — renders a white-label tenant's logo image with a graceful
 * load-error fallback.
 *
 * WHY (COMBO-001, 2026-06-12): an admin can publish a `logoUrl` that is a
 * plain `https://…` URL (the branding schema accepts any https URL — see
 * admin-branding-patch.ts — and onboarding/seed paths can set one too). The
 * Branding Studio's own preview only ever exercises a freshly-read data: URI,
 * so a *broken* https logo (404, hot-link blocked, wrong content-type) was
 * never caught before publish. On the firm's auth / onboarding / portal-shell
 * screens the raw `<img src={logoUrl}>` then renders the browser's
 * broken-image glyph — end clients see a broken mark on the login screen of
 * "their" firm, which reads as the wrong (or a defunct) company.
 *
 * This component renders the `<img>` but, on the FIRST load error, swaps to the
 * supplied `fallback` (the firm-name wordmark, or the Elreign mark) — exactly
 * what these surfaces already render when no logo is set. So a broken URL
 * degrades to the no-logo state instead of a broken-image icon. The error
 * latch resets when `src` changes, so replacing the logo re-attempts a load.
 */

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';

interface FirmLogoProps {
  /** The tenant logo URL (https URL or data: URI). */
  src: string;
  /** Accessible alt text for the logo image. */
  alt: string;
  /** Rendered instead of the image if the logo fails to load (or `src` empty). */
  fallback: ReactNode;
  style?: CSSProperties;
  className?: string;
}

export function FirmLogo({ src, alt, fallback, style, className }: FirmLogoProps) {
  const [errored, setErrored] = useState(false);

  // Reset the error latch whenever the source changes — a replaced logo
  // should be retried, not stay stuck on the previous failure.
  useEffect(() => {
    setErrored(false);
  }, [src]);

  if (!src || errored) {
    return <>{fallback}</>;
  }

  return (
    <img
      src={src}
      alt={alt}
      style={style}
      className={className}
      onError={() => setErrored(true)}
    />
  );
}
